import { useState } from 'react'
import { Notice } from '../Toast'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAssessments, getCycles, getPlans, getPrograms, setReportVisibility, updatePlan } from '../api/endpoints'
import type { PlanView } from '../api/types'
import { ErrorNotice } from '../components'
import HowItWorks from '../HowItWorks'

const toLocal = (iso: string | null) => (iso ? new Date(new Date(iso).getTime() - new Date(iso).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '')
const toIso = (local: string) => (local ? new Date(local).toISOString() : null)

export default function PlansPage() {
  const [cycleId, setCycleId] = useState('')
  const cycles = useQuery({ queryKey: ['cycles'], queryFn: getCycles })
  const programs = useQuery({ queryKey: ['programs'], queryFn: getPrograms })
  const assessments = useQuery({ queryKey: ['assessments'], queryFn: getAssessments })
  const plans = useQuery({ queryKey: ['plans', cycleId], queryFn: () => getPlans(Number(cycleId)), enabled: cycleId !== '' })
  const programName = (id: number | null) => (id === null ? 'Everyone else' : programs.data?.find((p) => p.id === id)?.code ?? String(id))
  const versionName = (id: number) => {
    for (const a of assessments.data ?? []) for (const v of a.versions) if (v.id === id) return `${a.name} v${v.versionNo}${v.status === 'DRAFT' ? ' (draft)' : ''}`
    return `Version ${id}`
  }
  return (
    <div>
      <h1>Schedule</h1>
      <HowItWorks id="schedule" title="How the schedule works" steps={[{ icon: 'schedule', title: "Open and close", text: "Set when each test can be started." },{ icon: 'shield', title: "Attempts and devices", text: "Decide how many attempts are allowed and whether a phone can be used." },{ icon: 'edit', title: "Individual exceptions", text: "Give extra attempts, extra time or a different device rule to one person, with a reason." }]} />
      <p className="muted">When each test opens and closes (your local time), how many attempts are allowed, and which devices may be used. Changes apply to attempts that have not started yet.</p>
      <div className="toolbar"><label>Cycle<select value={cycleId} onChange={(e) => setCycleId(e.target.value)}><option value="">Choose…</option>{cycles.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label></div>
      <ErrorNotice error={plans.error} />
      {cycleId !== '' && cycles.data?.find((c) => String(c.id) === cycleId) && <ReportSwitch cycle={cycles.data.find((c) => String(c.id) === cycleId)!} />}
      {plans.data?.map((p) => <PlanRow key={p.id} plan={p} program={programName(p.programId)} name={versionName(p.assessmentVersionId)} />)}
      {plans.data?.length === 0 && <p className="muted">No assessments are planned for this cycle.</p>}
    </div>
  )
}

function PlanRow({ plan, program, name }: { plan: PlanView; program: string; name: string }) {
  const queryClient = useQueryClient()
  const [opens, setOpens] = useState(toLocal(plan.opensAt))
  const [closes, setCloses] = useState(toLocal(plan.closesAt))
  const [attempts, setAttempts] = useState(String(plan.maxAttempts))
  const [mode, setMode] = useState(plan.deliveryMode ?? '')
  const [saved, setSaved] = useState(false)
  const save = useMutation({
    mutationFn: () => updatePlan(plan.id, { opensAt: toIso(opens), closesAt: toIso(closes), maxAttempts: Number(attempts), deliveryMode: mode || null }),
    onSuccess: async () => { setSaved(true); await queryClient.invalidateQueries({ queryKey: ['plans'] }) },
  })
  return (
    <form className="card" onSubmit={(e) => { e.preventDefault(); setSaved(false); save.mutate() }}>
      <h2>{program} · {plan.position}. {name}</h2>
      <div className="toolbar">
        <label>Opens<input type="datetime-local" value={opens} onChange={(e) => setOpens(e.target.value)} /></label>
        <label>Closes<input type="datetime-local" value={closes} onChange={(e) => setCloses(e.target.value)} /></label>
        <label>Attempts<input type="number" min={1} value={attempts} onChange={(e) => setAttempts(e.target.value)} style={{ width: 80 }} /></label>
        <label>Devices<select value={mode} onChange={(e) => setMode(e.target.value)}>
          <option value="">As set on the assessment</option><option value="SECURE_DESKTOP">Computers only</option><option value="MOBILE_ALLOWED">Computers, phones and tablets</option><option value="MOBILE_FIRST">Phone-first (reduced security)</option></select></label>
        <button type="submit" className="primary" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button>
      </div>
      <ErrorNotice error={save.error} />
      {saved && <Notice tone="ok">Saved.</Notice>}
    </form>
  )
}

/** Candidates see their own report only when this is on (default off). Turning it on prepares reports for everyone already scored. */
function ReportSwitch({ cycle }: { cycle: { id: number; name: string; reportVisibleToCandidates: boolean } }) {
  const queryClient = useQueryClient()
  const toggle = useMutation({
    mutationFn: (visible: boolean) => setReportVisibility(cycle.id, visible),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cycles'] }),
  })
  return (
    <section className="card">
      <h2>Candidate reports</h2>
      <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <input type="checkbox" style={{ width: 22, height: 22, minHeight: 0 }} checked={cycle.reportVisibleToCandidates} disabled={toggle.isPending}
          onChange={(e) => toggle.mutate(e.target.checked)} />
        <span>Let candidates in {cycle.name} download their own report</span>
      </label>
      <p className="muted" style={{ marginTop: 6 }}>Off by default. Candidates get a version without the admission band or panel notes, once their report is ready. You can turn this off again at any time.</p>
      <ErrorNotice error={toggle.error} />
    </section>
  )
}
